<template>
    <Head title="Create Ticket SLA Policy" />

    <div class="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div class="flex items-center gap-3">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create New SLA Policy</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define service level agreements for helpdesk tickets based on specific criteria.</p>
                        </div>
                        <InfoButton title="Creating SLA Policies" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                SLA Policies define response and resolution time targets for helpdesk tickets based on priority level.
                                Each priority (Low, Normal, High, Urgent) can have its own policy with specific time targets,
                                ensuring that critical issues receive faster responses while maintaining reasonable expectations
                                for lower priority tickets.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Policy Name:</strong> Descriptive name like "Urgent Priority SLA" or "Standard Support"<br>
                                • <strong>Priority:</strong> Select the priority level (Low, Normal, High, or Urgent) this policy applies to<br>
                                • <strong>Response Time:</strong> Hours until ticket must be acknowledged or assigned to a technician<br>
                                • <strong>Resolution Time:</strong> Hours until ticket must be fully resolved and closed<br>
                                • <strong>Active Status:</strong> Enable to make the policy available for use immediately
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Start with defaults</strong> - Create one policy per priority level (Urgent=1hr/4hr, High=4hr/8hr,
                                  Normal=8hr/24hr, Low=24hr/72hr)<br>
                                • <strong>Response vs Resolution</strong> - Response is initial acknowledgment, resolution is full completion<br>
                                • <strong>Be realistic</strong> - Set achievable targets based on your team's capacity<br>
                                • <strong>Business hours</strong> - SLA times are calculated based on business hours by default<br>
                                • <strong>Monitor compliance</strong> - Review SLA performance regularly and adjust as needed
                            </p>
                        </InfoButton>
                    </div>
                </div>

                <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                    <!-- Name Field -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300 dark:border-red-500': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.name }}
                        </p>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300 dark:border-red-500': form.errors.description }"
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.description }}
                        </p>
                    </div>

                    <!-- Criteria Section -->
                    <div class="bg-gray-50 dark:bg-gray-700/50 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Leave criteria blank to apply to all tickets. More specific policies will take precedence.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Priority -->
                            <div>
                                <label for="priority" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    v-model="form.priority"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.priority }"
                                >
                                    <option value="">All Priorities</option>
                                    <option value="low">Low</option>
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                                <p v-if="form.errors.priority" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.priority }}
                                </p>
                            </div>

                            <!-- Ticket Category -->
                            <div>
                                <label for="category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Ticket Category
                                </label>
                                <select
                                    id="category_id"
                                    v-model="form.category_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.category_id }"
                                >
                                    <option value="">All Categories</option>
                                    <option v-for="category in categories" :key="category.id" :value="category.id">
                                        {{ category.name }}
                                    </option>
                                </select>
                                <p v-if="form.errors.category_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.category_id }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- SLA Times Section -->
                    <div class="bg-gray-50 dark:bg-gray-700/50 -mx-6 px-6 py-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">SLA Time Targets</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Define time limits in hours for response and resolution.</p>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <!-- Response Time -->
                            <div>
                                <label for="response_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Response Time (hours)
                                </label>
                                <input
                                    id="response_time_hours"
                                    v-model="form.response_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 4.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.response_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Time until ticket must be acknowledged/assigned</p>
                                <p v-if="form.errors.response_time_hours" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.response_time_hours }}
                                </p>
                            </div>

                            <!-- Resolution Time -->
                            <div>
                                <label for="resolution_time_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Resolution Time (hours)
                                </label>
                                <input
                                    id="resolution_time_hours"
                                    v-model="form.resolution_time_hours"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="e.g., 24.00"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    :class="{ 'border-red-300 dark:border-red-500': form.errors.resolution_time_hours }"
                                />
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Time until ticket must be resolved</p>
                                <p v-if="form.errors.resolution_time_hours" class="mt-1 text-sm text-red-600 dark:text-red-400">
                                    {{ form.errors.resolution_time_hours }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Is Active Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                            <p class="text-gray-500 dark:text-gray-400">Make this policy available for matching immediately.</p>
                        </div>
                    </div>

                    <!-- Info Box -->
                    <div class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <svg class="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="ml-3 flex-1">
                                <p class="text-sm text-blue-700 dark:text-blue-300">
                                    <strong>Specificity Matching:</strong> Policies with more specific criteria (category + priority) will be matched first. For example, a policy for "Hardware > Urgent" will be used instead of a general "Urgent" policy.
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Link
                            :href="route('settings.tickets.sla.index')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Back to List
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="form.processing">Creating...</span>
                            <span v-else>Create SLA Policy</span>
                        </button>
                    </div>
                </form>
            </div>
    </div>
</template>

<script setup>
import { Head, Link, router } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    categories: Array,
});

const toast = useToast();

const form = useSecureForm({
    name: '',
    description: '',
    category_id: '',
    priority: '',
    response_time_hours: '',
    resolution_time_hours: '',
    is_active: true,
});

const submit = () => {
    // Convert empty strings to null for optional fields
    const data = {
        ...form.data(),
        category_id: form.category_id || null,
        priority: form.priority || null,
    };

    form.transform(() => data).post(route('settings.tickets.sla.store'), {
        onSuccess: () => {
            toast.success('SLA policy created successfully.');
            router.visit(route('settings.tickets.sla.index'));
        },
        onError: (errors) => {
            console.error('SLA Policy creation errors:', errors);

            // Show specific error if available
            if (errors.error) {
                toast.error(errors.error);
            } else {
                const firstError = Object.values(errors)[0];
                if (firstError) {
                    toast.error(firstError);
                } else {
                    toast.error('Failed to create SLA policy. Please check the form for errors.');
                }
            }
        }
    });
};
</script>
